import React, { useState } from 'react';
import { User, Lock, ArrowRight, Mail, Phone, MapPin, Calendar, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { ListenInsteadOfReading } from '../components/ListenInsteadOfReading';
export function LoginRegister() {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1500);
  };
  return <div className="min-h-screen bg-gray-50 pt-40 lg:pt-52 pb-20" data-id="element-1191">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1192">
        {/* Page Header */}
        <div className="text-center mb-8" data-id="element-1193">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1194">
            Student Portal
          </h1>
          <p className="text-lg text-gray-600" data-id="element-1195">
            Access your on-demand classes or register to create an account
          </p>
        </div>

        {/* Important Notice */}
        <div className="mb-8 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl flex items-start gap-3 max-w-2xl mx-auto" data-id="element-1196">
          <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1197" />
          <div data-id="element-1198">
            <p className="text-sm font-semibold text-blue-900 mb-1" data-id="element-1199">
              For On-Demand Classes Only
            </p>
            <p className="text-sm text-blue-800" data-id="element-1200">
              This registration is for <strong data-id="element-1201">On-Demand Classes only</strong>.
              If you're taking Zoom or In-Person classes, you do not need to
              register here.
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-8" data-id="element-1202">
          <div className="inline-flex bg-white rounded-lg shadow-sm border border-gray-200 p-1" data-id="element-1203">
            <button onClick={() => setActiveTab('login')} className={`px-8 py-3 rounded-md font-semibold transition-all ${activeTab === 'login' ? 'bg-brand-purple-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1204">
              Log In
            </button>
            <button onClick={() => setActiveTab('register')} className={`px-8 py-3 rounded-md font-semibold transition-all ${activeTab === 'register' ? 'bg-brand-purple-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1205">
              Register
            </button>
          </div>
        </div>

        {/* Login Form */}
        {activeTab === 'login' && <Card className="max-w-md mx-auto p-8" data-id="element-1206">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-1207">
              Log In to Your Account
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1208">
              <div data-id="element-1209">
                <label htmlFor="login-username" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1210">
                  Username
                </label>
                <div className="relative" data-id="element-1211">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1212">
                    <User className="h-5 w-5 text-gray-400" data-id="element-1213" />
                  </div>
                  <input type="text" id="login-username" className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" placeholder="Enter your username" required data-id="element-1214" />
                </div>
              </div>

              <div data-id="element-1215">
                <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1216">
                  Password
                </label>
                <div className="relative" data-id="element-1217">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1218">
                    <Lock className="h-5 w-5 text-gray-400" data-id="element-1219" />
                  </div>
                  <input type="password" id="login-password" className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" placeholder="••••••••" required data-id="element-1220" />
                </div>
              </div>

              <Button type="submit" className="w-full font-semibold" size="lg" isLoading={isLoading} data-id="element-1221">
                Log In <ArrowRight className="ml-2 h-4 w-4" data-id="element-1222" />
              </Button>
            </form>

            <div className="mt-6 text-center" data-id="element-1223">
              <a href="/14.html" className="text-sm text-gray-600 hover:text-brand-purple-600 hover:underline" data-id="element-1224">
                Forgot your password?
              </a>
            </div>
          </Card>}

        {/* Registration Form */}
        {activeTab === 'register' && <Card className="max-w-2xl mx-auto p-8" data-id="element-1225">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center" data-id="element-1226">
              Create Your Account
            </h2>
            <p className="text-sm text-gray-600 mb-6 text-center" data-id="element-1227">
              Register for on-demand classes
            </p>

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1228">
              {/* Personal Information */}
              <div data-id="element-1229">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1230">
                  <User className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1231" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1232">
                  <div data-id="element-1233">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1234">
                      Title
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1235">
                      <option data-id="element-1236">Mr.</option>
                      <option data-id="element-1237">Ms.</option>
                      <option data-id="element-1238">Mrs.</option>
                      <option data-id="element-1239">Dr.</option>
                    </select>
                  </div>
                  <div data-id="element-1240">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1241">
                      First Name<span className="text-red-500" data-id="element-1242">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1243" />
                  </div>
                  <div data-id="element-1244">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1245">
                      Last Name<span className="text-red-500" data-id="element-1246">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1247" />
                  </div>
                  <div data-id="element-1248">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1249">
                      Company
                    </label>
                    <input type="text" className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1250" />
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div data-id="element-1251">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1252">
                  <MapPin className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1253" />
                  Billing Address
                </h3>
                <div className="space-y-4" data-id="element-1254">
                  <div data-id="element-1255">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1256">
                      Address 1<span className="text-red-500" data-id="element-1257">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1258" />
                  </div>
                  <div data-id="element-1259">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1260">
                      Address 2
                    </label>
                    <input type="text" className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1261" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1262">
                    <div data-id="element-1263">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1264">
                        City<span className="text-red-500" data-id="element-1265">*</span>
                      </label>
                      <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1266" />
                    </div>
                    <div data-id="element-1267">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1268">
                        State/Province<span className="text-red-500" data-id="element-1269">*</span>
                      </label>
                      <select required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1270">
                        <option value="" data-id="element-1271">Select...</option>
                        <option value="AZ" data-id="element-1272">Arizona</option>
                        <option value="CA" data-id="element-1273">California</option>
                        <option value="NV" data-id="element-1274">Nevada</option>
                      </select>
                    </div>
                    <div data-id="element-1275">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1276">
                        Zip<span className="text-red-500" data-id="element-1277">*</span>
                      </label>
                      <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1278" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div data-id="element-1279">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1280">
                  <Phone className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1281" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1282">
                  <div data-id="element-1283">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1284">
                      Phone<span className="text-red-500" data-id="element-1285">*</span>
                    </label>
                    <input type="tel" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1286" />
                  </div>
                  <div data-id="element-1287">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1288">
                      Email<span className="text-red-500" data-id="element-1289">*</span>
                    </label>
                    <input type="email" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1290" />
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div data-id="element-1291">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1292">
                  Additional Information
                </h3>
                <div className="space-y-4" data-id="element-1293">
                  <div data-id="element-1294">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1295">
                      How did you hear about us?
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1296">
                      <option value="" data-id="element-1297">Select...</option>
                      <option data-id="element-1298">Google Search</option>
                      <option data-id="element-1299">Social Media</option>
                      <option data-id="element-1300">Referral</option>
                      <option data-id="element-1301">Other</option>
                    </select>
                  </div>
                  <div data-id="element-1302">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1303">
                      Are you on Facebook?
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1304">
                      <option value="" data-id="element-1305">Select...</option>
                      <option data-id="element-1306">Yes</option>
                      <option data-id="element-1307">No</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Username & Password */}
              <div data-id="element-1308">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1309">
                  <Lock className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1310" />
                  Create Your Username & Password
                </h3>
                <div className="space-y-4" data-id="element-1311">
                  <div data-id="element-1312">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1313">
                      Username<span className="text-red-500" data-id="element-1314">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1315" />
                  </div>
                  <div data-id="element-1316">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1317">
                      Password<span className="text-red-500" data-id="element-1318">*</span>
                    </label>
                    <input type="password" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1319" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1320">
                      Must be at least 8 characters
                    </p>
                  </div>
                  <div data-id="element-1321">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1322">
                      Confirm Password<span className="text-red-500" data-id="element-1323">*</span>
                    </label>
                    <input type="password" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1324" />
                  </div>
                </div>
              </div>

              {/* License Expiration Date */}
              <div data-id="element-1325">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1326">
                  <Calendar className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1327" />
                  License Expiration Date
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-1328">
                  <div data-id="element-1329">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1330">
                      Month
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1331">
                      <option data-id="element-1332">January</option>
                      <option data-id="element-1333">February</option>
                      <option data-id="element-1334">March</option>
                      <option data-id="element-1335">April</option>
                      <option data-id="element-1336">May</option>
                      <option data-id="element-1337">June</option>
                      <option data-id="element-1338">July</option>
                      <option data-id="element-1339">August</option>
                      <option data-id="element-1340">September</option>
                      <option data-id="element-1341">October</option>
                      <option data-id="element-1342">November</option>
                      <option data-id="element-1343">December</option>
                    </select>
                  </div>
                  <div data-id="element-1344">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1345">
                      Year
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1346">
                      <option data-id="element-1347">2024</option>
                      <option data-id="element-1348">2025</option>
                      <option data-id="element-1349">2026</option>
                      <option data-id="element-1350">2027</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-2" data-id="element-1351">
                  <a href="#" className="hover:underline" data-id="element-1352">
                    Click here to obtain your license expiration date from ADRE
                  </a>
                </p>
              </div>

              {/* Identity Confirmation */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-1353">
                <div className="flex items-start" data-id="element-1354">
                  <input type="checkbox" id="identity-confirm" required className="mt-1 h-4 w-4 text-brand-purple-600 focus:ring-brand-purple-500 border-gray-300 rounded" data-id="element-1355" />
                  <label htmlFor="identity-confirm" className="ml-3 text-sm text-gray-700" data-id="element-1356">
                    <strong data-id="element-1357">I affirm this information to be true.</strong> The
                    Arizona Department of Real Estate requires that we confirm
                    your information and that you are the person taking the
                    classes. Incorporating a real person's name and license
                    number into a registration without the permission of both
                    parties, if you are NOT the person named in the
                    registration, we strongly recommend that you log out and not
                    continue. We reserve the right to use reasonable means to
                    verify your identity.
                  </label>
                </div>
              </div>

              {/* Privacy Statement */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1358">
                <h4 className="font-semibold text-blue-900 mb-2" data-id="element-1359">
                  Privacy Statement
                </h4>
                <p className="text-sm text-blue-800 mb-2" data-id="element-1360">
                  By registering with Desert Sage Seminars, you will create an
                  account by which we can administer your real estate license
                  renewal classes. Your information will be kept confidential
                  and not shared with any other party.
                </p>
                <p className="text-sm text-blue-800 mb-2" data-id="element-1361">
                  The Arizona Department of Real Estate requires that we confirm
                  your information and that you are the person taking the
                  classes.
                </p>
                <p className="text-sm text-blue-800" data-id="element-1362">
                  Payments for our classes are made through a 3rd party merchant
                  account secured by our site, and they do not share your credit
                  card information with us or any other party.
                </p>
              </div>

              {/* Submit Button */}
              <Button type="submit" className="w-full font-semibold" size="lg" isLoading={isLoading} data-id="element-1363">
                Create Account <CheckCircle2 className="ml-2 h-5 w-5" data-id="element-1364" />
              </Button>
            </form>
          </Card>}

        <ListenInsteadOfReading data-id="element-1365" />
      </div>
    </div>;
}